<template>
  <div class="regularizations-view">
    <div class="page-header">
      <PageTitle title="Produits à régulariser" />
    </div>

    <div class="card">
      <div class="toolbar">
        <div class="search-container">
          <input type="text" v-model="searchQuery" @input="onSearchInput" class="search-input"
            placeholder="Rechercher par référence ou nom du produit..." />
        </div>
        <div class="filters">
          <label class="filter-item">
            <span>Statut</span>
            <select v-model="filters.status" @change="onFiltersChange">
              <option value="">Tous</option>
              <option value="pending">En attente</option>
              <option value="reconciled">Réconciliée</option>
              <option value="cancelled">Annulée</option>
            </select>
          </label>
          <label class="filter-item checkbox">
            <input type="checkbox" v-model="filters.pending_only" @change="onFiltersChange" />
            <span>En attente seulement</span>
          </label>
        </div>
      </div>

      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>Chargement des régularisations...</p>
      </div>

      <div v-else>
        <div v-if="regularizations.length === 0" class="empty">
          <p>Aucune régularisation trouvée</p>
        </div>
        <div v-else class="table-wrapper">
          <table class="table">
            <thead>
              <tr>
                <th>Réf</th>
                <th>Produit</th>
                <th>Quantité</th>
                <th>Stock actuel</th>
                <th>Créée le</th>
                <th>Statut</th>
                <th>Par</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in regularizations" :key="r.id">
                <td>REG-{{ r.id }}</td>
                <td>{{ r.product?.name || '-' }}</td>
                <td>{{ r.quantity || '-' }}</td>
                <td>
                  <span v-if="r.product && r.product.stock_quantity !== null && r.product.stock_quantity !== undefined"
                    class="stock-value" :class="{ 'low-stock': r.status === 'pending' && !canReconcile(r) }">
                    {{ r.product.stock_quantity }}
                  </span>
                  <span v-else class="stock-value no-stock">-</span>
                </td>
                <td>{{ formatDate(r.created_at) }}</td>
                <td>
                  <span class="badge" :class="getStatusClass(r.status)">
                    {{ getStatusLabel(r.status) }}
                  </span>
                </td>
                <td>{{ r.user?.name || '-' }}</td>
                <td class="actions">
                  <div class="actions-group">
                    <button v-if="r.status === 'pending'" class="btn-reconcile" @click="openReconcileModal(r)"
                      :disabled="reconciling[r.id] || !canReconcile(r)"
                      :title="!canReconcile(r) ? getReconcileDisabledReason(r) : ''">
                      <i v-if="reconciling[r.id]" class="fa-solid fa-spinner fa-spin"></i>
                      <i v-else class="fa-solid fa-check-circle"></i>
                      Régulariser
                    </button>
                    <button class="link-btn" @click="viewProduct(r.product_id)" v-if="r.product_id">Voir
                      produit</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <PaginationComponent :currentPage="pagination.page" :perPage="Number(pagination.per_page)"
          :total="pagination.total"
          :to="pagination.to || Math.min(pagination.page * Number(pagination.per_page), pagination.total)"
          :showPageNumbers="true" :maxVisiblePages="5" @page-change="goToPage" />
      </div>
    </div>

    <!-- Modal de réconciliation -->
    <transition name="modal">
      <div v-if="showReconcileModal" class="modal-overlay" @click="closeReconcileModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <div class="modal-icon info">
              <i class="fa-solid fa-info-circle"></i>
            </div>
            <h3>Régulariser la régularisation</h3>
            <button @click="closeReconcileModal" class="modal-close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="selectedRegularization" class="reconcile-info">
              <div class="info-item">
                <span class="info-label">Référence:</span>
                <span class="info-value">REG-{{ selectedRegularization.id }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Produit:</span>
                <span class="info-value">{{ selectedRegularization.product?.name || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Quantité à retirer:</span>
                <span class="info-value quantity">{{ Math.abs(selectedRegularization.quantity) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Stock actuel:</span>
                <span class="info-value stock"
                  :class="{ 'low-stock': (selectedRegularization.product?.stock_quantity || 0) < Math.abs(selectedRegularization.quantity) }">
                  {{ selectedRegularization.product?.stock_quantity || 0 }}
                </span>
              </div>
              <div
                v-if="selectedRegularization.product && (selectedRegularization.product.stock_quantity || 0) < Math.abs(selectedRegularization.quantity)"
                class="warning-message">
                <i class="fa-solid fa-exclamation-triangle"></i>
                <span>Attention: Le stock actuel est insuffisant pour cette régularisation.</span>
              </div>
            </div>
            <div class="form-group">
              <label for="reconcile-notes">Notes (optionnel)</label>
              <textarea id="reconcile-notes" v-model="reconcileNotes" class="form-textarea"
                placeholder="Ajouter des notes sur la réconciliation..." rows="3"></textarea>
            </div>
            <p class="modal-description">
              Cette action va retirer la quantité du stock et marquer la régularisation comme réconciliée.
            </p>
          </div>
          <div class="modal-actions">
            <button class="modal-btn secondary" @click="closeReconcileModal" :disabled="isReconciling">
              Annuler
            </button>
            <button class="modal-btn primary" @click="confirmReconcile"
              :disabled="isReconciling || (selectedRegularization && !canReconcile(selectedRegularization))"
              :title="selectedRegularization && !canReconcile(selectedRegularization) ? getReconcileDisabledReason(selectedRegularization) : ''">
              <i v-if="isReconciling" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-check"></i>
              {{ isReconciling ? 'Réconciliation...' : 'Confirmer la régularisation' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import productRegularizationService from '@/services/productRegularizationService'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import PageTitle from '@/components/common/PageTitle.vue'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'ProductRegularizationsView',
  components: { PaginationComponent, PageTitle },
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()
    const loading = ref(false)
    const regularizations = ref([])
    const searchQuery = ref('')
    const searchTimeout = ref(null)
    const showReconcileModal = ref(false)
    const selectedRegularization = ref(null)
    const reconcileNotes = ref('')
    const isReconciling = ref(false)
    const reconciling = ref({})
    const filters = ref({
      status: '',
      pending_only: true,
      per_page: 10
    })
    const pagination = ref({
      page: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0
    })

    const fetchRegularizations = async () => {
      try {
        loading.value = true
        const res = await productRegularizationService.getRegularizations({
          status: filters.value.status || undefined,
          pending_only: !!filters.value.pending_only,
          per_page: filters.value.per_page,
          page: pagination.value.page,
          search: searchQuery.value.trim() || undefined
        })
        const data = res.data
        const list = Array.isArray(data) ? data : (data.data || data.items || [])
        regularizations.value = list
        pagination.value.total = Number(data.total || (data.meta && data.meta.total) || list.length) || 0
        pagination.value.per_page = Number(data.per_page || (data.meta && data.meta.per_page) || filters.value.per_page) || 10
        pagination.value.page = Number(data.current_page || (data.meta && data.meta.current_page) || pagination.value.page) || 1
        pagination.value.from = Number(data.from || (data.meta && data.meta.from) || ((pagination.value.page - 1) * pagination.value.per_page + 1)) || 0
        pagination.value.to = Number(data.to || (data.meta && data.meta.to) || Math.min(pagination.value.page * pagination.value.per_page, pagination.value.total)) || 0
      } catch (e) {
        console.error('Erreur chargement régularisations:', e)
        regularizations.value = []
      } finally {
        loading.value = false
      }
    }

    const onFiltersChange = () => {
      pagination.value.page = 1
      fetchRegularizations()
    }

    const onSearchInput = () => {
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        pagination.value.page = 1
        fetchRegularizations()
      }, 500)
    }

    const goToPage = (page) => {
      pagination.value.page = page
      fetchRegularizations()
    }

    const viewProduct = (productId) => {
      router.push({ name: 'product-detail', params: { id: productId } })
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' })
    }

    const getStatusLabel = (status) => {
      const map = {
        pending: 'En attente',
        reconciled: 'Réconciliée',
        cancelled: 'Annulée'
      }
      return map[status] || status
    }

    const getStatusClass = (status) => {
      const map = {
        pending: 'badge-warning',
        reconciled: 'badge-success',
        cancelled: 'badge-muted'
      }
      return map[status] || ''
    }

    const canReconcile = (regularization) => {
      // Vérifier si le statut est pending
      if (regularization.status !== 'pending') {
        return false
      }

      // Vérifier si le produit existe
      if (!regularization.product) {
        return false
      }

      // Vérifier si le produit suit l'inventaire
      if (!regularization.product.track_inventory) {
        return false
      }

      // Vérifier si le stock est suffisant
      const quantityToDeduct = Math.abs(regularization.quantity || 0)
      const currentStock = regularization.product.stock_quantity || 0

      if (currentStock < quantityToDeduct) {
        return false
      }

      return true
    }

    const getReconcileDisabledReason = (regularization) => {
      if (regularization.status !== 'pending') {
        return 'Cette régularisation a déjà été réconciliée ou annulée'
      }

      if (!regularization.product) {
        return 'Produit non trouvé'
      }

      if (!regularization.product.track_inventory) {
        return 'Ce produit ne suit pas l\'inventaire'
      }

      const quantityToDeduct = Math.abs(regularization.quantity || 0)
      const currentStock = regularization.product.stock_quantity || 0

      if (currentStock < quantityToDeduct) {
        return `Stock insuffisant (disponible: ${currentStock}, requis: ${quantityToDeduct})`
      }

      return ''
    }

    const openReconcileModal = (regularization) => {
      selectedRegularization.value = regularization
      reconcileNotes.value = ''
      showReconcileModal.value = true
    }

    const closeReconcileModal = () => {
      if (isReconciling.value) return
      showReconcileModal.value = false
      selectedRegularization.value = null
      reconcileNotes.value = ''
    }

    const confirmReconcile = async () => {
      if (!selectedRegularization.value) return

      const regularizationId = selectedRegularization.value.id
      isReconciling.value = true
      reconciling.value[regularizationId] = true

      try {
        await productRegularizationService.reconcileRegularization(regularizationId, {
          notes: reconcileNotes.value.trim()
        })

        toastStore.addToast({
          type: 'success',
          message: 'Régularisation réconciliée avec succès',
          duration: 3000
        })

        closeReconcileModal()
        fetchRegularizations()
      } catch (error) {
        console.error('Erreur lors de la réconciliation:', error)
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Erreur lors de la réconciliation'

        toastStore.addToast({
          type: 'error',
          message: errorMessage,
          duration: 4000
        })
      } finally {
        isReconciling.value = false
        reconciling.value[regularizationId] = false
      }
    }

    // initial load
    fetchRegularizations()

    // Nettoyer le timeout lors du démontage
    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    return {
      loading,
      regularizations,
      searchQuery,
      filters,
      pagination,
      showReconcileModal,
      selectedRegularization,
      reconcileNotes,
      isReconciling,
      reconciling,
      fetchRegularizations,
      onFiltersChange,
      onSearchInput,
      goToPage,
      viewProduct,
      formatDate,
      getStatusLabel,
      getStatusClass,
      canReconcile,
      getReconcileDisabledReason,
      openReconcileModal,
      closeReconcileModal,
      confirmReconcile
    }
  }
})
</script>

<style scoped>
.regularizations-view {
  padding: 0 0 24px 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.page-heading {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.card {
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-bottom: 1.5px solid #f1f5f9;
  background: #fafbfc;
  flex-wrap: wrap;
}

.search-container {
  flex: 1;
  min-width: 300px;
  max-width: 500px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  color: #334155;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #334155;
}

.filter-item select {
  padding: 6px 8px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: white;
}

.filter-item.checkbox input {
  margin-right: 6px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f1f5f9;
  border-top: 3px solid #0A5C6B;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty {
  padding: 24px;
  text-align: center;
  color: #64748b;
}

.table-wrapper {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table thead {
  background: #f8fafc;
}

.table th,
.table td {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  font-size: 14px;
  color: #1e293b;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
}

.badge-success {
  background: #d1fae5;
  color: #065f46;
}

.badge-muted {
  background: #e5e7eb;
  color: #374151;
}

.badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.actions-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.link-btn {
  background: none;
  border: none;
  color: #0A5C6B;
  cursor: pointer;
  font-weight: 600;
}

.link-btn:hover {
  text-decoration: underline;
}

.stock-value {
  font-weight: 600;
  color: #16a34a;
}

.stock-value.low-stock {
  color: #dc2626;
}

.stock-value.no-stock {
  color: #94a3b8;
  font-weight: normal;
}

.btn-reconcile {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-reconcile:hover:not(:disabled) {
  background: linear-gradient(135deg, #15803d 0%, #166534 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.3);
}

.btn-reconcile:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideUp 0.3s ease-out;
  max-height: 90vh;
  overflow-y: auto;
}

@keyframes modalSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1.5px solid #e2e8f0;
  position: relative;
}

.modal-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-icon.info {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}

.modal-icon i {
  font-size: 24px;
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  flex: 1;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e2e8f0;
  color: #475569;
}

.modal-close svg {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

.modal-body {
  padding: 24px;
}

.reconcile-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.info-label {
  font-weight: 600;
  color: #64748b;
}

.info-value {
  font-weight: 700;
  color: #0f172a;
}

.info-value.quantity {
  color: #dc2626;
}

.info-value.stock {
  color: #16a34a;
}

.info-value.stock.low-stock {
  color: #dc2626;
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #92400e;
  font-size: 13px;
  font-weight: 600;
  margin-top: 8px;
}

.warning-message i {
  font-size: 16px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: #334155;
  resize: vertical;
  transition: all 0.2s;
}

.form-textarea:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.form-textarea::placeholder {
  color: #94a3b8;
}

.modal-description {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1.5px solid #e2e8f0;
}

.modal-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-btn.secondary {
  background: #f1f5f9;
  color: #475569;
}

.modal-btn.secondary:hover:not(:disabled) {
  background: #e2e8f0;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white;
}

.modal-btn.primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
